import type {BundledLanguage, BundledTheme, HighlighterGeneric} from "shiki";

import {cn} from "@heroui/react";
import {use} from "react";

import {useTheme} from "../../hooks/use-theme";

type Highlighter = HighlighterGeneric<BundledLanguage, BundledTheme>;

// Singleton highlighter promise — initialised once and reused across renders
let highlighterPromise: Promise<Highlighter> | null = null;

function getHighlighterPromise(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import("shiki").then(({createHighlighter}) =>
      createHighlighter({
        langs: ["css"],
        themes: ["github-dark", "github-light"],
      }),
    );
  }

  return highlighterPromise;
}

interface HighlightedCodeProps {
  code: string;
  lang?: string;
  showLineNumbers?: boolean;
  className?: string;
}

/**
 * Syntax-highlighted code block using Shiki.
 * Must be rendered inside a <Suspense> boundary.
 * Uses React 19's `use()` hook to suspend while the highlighter loads.
 */
export function HighlightedCode({
  className,
  code,
  lang = "css",
  showLineNumbers = false,
}: HighlightedCodeProps) {
  const {theme} = useTheme();

  // Suspend until the highlighter is ready
  const highlighter = use(getHighlighterPromise());

  const highlightedHtml = (() => {
    const trimmedCode = code.trim();

    if (!trimmedCode) return "";

    const loadedLangs = highlighter.getLoadedLanguages();
    const langToUse = loadedLangs.includes(lang as BundledLanguage) ? lang : "text";

    return highlighter.codeToHtml(trimmedCode, {
      lang: langToUse,
      theme: theme === "dark" ? "github-dark" : "github-light",
    });
  })();

  if (!highlightedHtml) return null;

  return (
    <div
      dangerouslySetInnerHTML={{__html: highlightedHtml}}
      className={cn(
        "overflow-auto text-sm [&_.shiki]:bg-transparent! [&_pre]:bg-transparent!",
        "[&>pre]:m-0 [&>pre]:p-4",
        "[&>pre>code]:block [&>pre>code]:font-mono [&>pre>code]:leading-relaxed",
        showLineNumbers && "code-line-numbers",
        className,
      )}
    />
  );
}
